import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { FolderOpen, Star, Plus, Edit, Trash2 } from 'lucide-react';

function ProjectGroupGrid({ 
  projectGroups, 
  onAddProjectGroup, 
  onEditProjectGroup, 
  onDeleteProjectGroup,
  onAddProject 
}) {
  return (
    <div className="lg:col-span-2">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <FolderOpen className="w-5 h-5 mr-2 text-red-600" />
          Project Groups
        </h2>
        <Button variant="outline" size="sm">
          View All
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {projectGroups.map((group) => (
          <Card key={group.id} className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  <CardTitle className="text-lg">{group.name}</CardTitle>
                  {group.is_favorite && (
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  )}
                </div>
                <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditProjectGroup(group);
                    }}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteProjectGroup(group);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <CardDescription className="line-clamp-2">
                {group.description || "No description"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                <span>{group.projects?.length || 0} projects</span>
                <span>
                  {group.projects?.reduce((acc, project) => acc + (project.tasks?.length || 0), 0) || 0} tasks
                </span>
              </div>
              
              {/* Quick project preview */}
              {group.projects?.slice(0, 2).map((project) => (
                <div key={project.id} className="mt-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{project.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{project.tasks?.length || 0} tasks</span>
                  </div>
                </div>
              ))}
              
              {/* Add Project Button */}
              <Button
                variant="outline"
                size="sm"
                className="mt-3 w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddProject(group.id);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Project
              </Button>
            </CardContent>
          </Card>
        ))}

        {/* Add New Project Group Card */}
        <Card 
          className="border-dashed border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors cursor-pointer"
          onClick={onAddProjectGroup}
        >
          <CardContent className="flex flex-col items-center justify-center h-48 text-center">
            <Plus className="w-8 h-8 text-gray-400 mb-2" />
            <h3 className="font-medium text-gray-600 dark:text-gray-300">Add Project Group</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Create a new project group to organize your work</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ProjectGroupGrid;
